/**
 * `/fly`'s scene, as a contract: everything a route needs to describe to
 * `createFlightScene` and everything the scene hands back, with none of the
 * three.js that implements it.
 *
 * Split out of `flight-scene.ts` so the imperative half stays the renderer
 * lifecycle, the frame loop and the scene graph. Nothing here imports `three`,
 * so a test (or a future non-WebGPU consumer) can talk about the scene's
 * callbacks without pulling a GPU backend into the module graph.
 */

import type {
  CareerDate,
  EncounterState,
  FlightInputs,
  GroundQuery,
  SceneryQuery,
  MissionRegion,
  MissionRun,
  MissionSpec,
  StickShapingId,
} from "@red-opus/engine";
import type { PersonalPaint } from "@red-opus/formats";
import type { AudioSettings } from "../audio/settings.ts";
import type { BootProgress } from "./boot-progress.ts";
import type { CameraMode } from "./camera-modes.ts";
import type { CameraTarget } from "./camera-target.ts";
import type { CombatHud } from "./combat-hud.ts";
import { NEUTRAL_SURFACES, type ControlSurfaceState } from "./cockpit-frame.ts";
import type { DashCanvasFeed } from "./cockpit-dash-quad.ts";
import type { CockpitHitMarks } from "./cockpit-hits.ts";
import type { EffectsSettings, SkyTreatment } from "./effects-settings.ts";
import type { FreeLookFrame } from "./free-look.ts";
import type { FlightRegionSummary } from "./fly-region.ts";
import type { FlightHud } from "./hud-snapshot.ts";
import type { GlanceDirection } from "./keys.ts";
import type { KillCamPlacement } from "./killcam-layout.ts";
import type { MissionHud } from "./mission-hud.ts";
import type { PadFrame } from "./gamepad-axes.ts";
import type { RendererFacts, RendererPlan } from "../three/renderer-mode.ts";
import type { PointerFrame } from "./pointer.ts";
import type { EncounterRunner } from "./sim-runner.ts";

export type FlightSceneStatus = "initializing" | "ready" | "error";

/**
 * What the cockpit panel needs every frame rather than every HUD update: which
 * way the pilot is looking (which swaps the whole painted composition, not
 * just the camera), whether the muzzle flash is lit right now, and the live
 * aileron/elevator/rudder deflection. All three change far faster than the
 * 12 Hz HUD — the flash strobes with the rounds, the surfaces with the stick —
 * so they ride their own callback, fired only on a change.
 */
export interface CockpitViewState {
  readonly glance: GlanceDirection;
  readonly gunFlash: boolean;
  readonly surfaces: ControlSurfaceState;
  /**
   * Which of the eight being-hit overlays this flight has accumulated, as
   * `cockpit-hits.ts`'s bitmask. A mask rather than a list of ops because the
   * overlays themselves live in the aircraft's baked art, which the flight
   * loop does not hold — and because it is 8 bits, so the panel's
   * change-detection can compare it with `===`.
   */
  readonly hitMarks: CockpitHitMarks;
  /**
   * Whether a right-mouse free look is live this frame — held, or still easing
   * home (`free-look.ts`). The camera reads the *angle*; this is the one bit
   * the painted panel needs, because the panel cannot follow: it is a fixed
   * forward composition, and a glance the art has no picture for is shown by
   * showing no panel at all (the `up` glance's own rule, `cockpit-frame.ts`).
   *
   * Only the painted cockpit cares. In 3D COCKPIT the dashboard is geometry in
   * the scene, so looking away from it is looking away from it, and
   * `FlightSurface.tsx` leaves the panel composing for the quad.
   */
  readonly freeLook: boolean;
}

/** Nothing held, no flash, stick centred, cockpit unmarked — what a route shows before the scene's first frame. */
export const IDLE_COCKPIT_VIEW: CockpitViewState = {
  glance: "forward",
  gunFlash: false,
  surfaces: NEUTRAL_SURFACES,
  hitMarks: 0,
  freeLook: false,
};

export interface FlightSceneCallbacks {
  readonly onStatus: (status: FlightSceneStatus, message?: string) => void;
  /**
   * Which step of the boot is starting now, and what share of the wait is
   * already behind it (`boot-progress.ts`). Fired once per phase, in order,
   * strictly before `onStatus("ready")` — so a loading screen can report the
   * load rather than animate over it. Optional: `/duel` and `/vcr` show their
   * own furniture and take the status alone.
   */
  readonly onBootProgress?: (progress: BootProgress) => void;
  /**
   * Which backend this flight actually came up on, fired once — before the
   * first phase on the ordinary path, and again with `source: "fallback"` if a
   * WebGPU `init()` rejected and the WebGL renderer took over
   * (`three/renderer-open.ts`).
   *
   * A callback rather than a field on `FlightScene` because the loading screen
   * wants it *while* the boot is running, which is before there is a handle to
   * read a field off.
   */
  readonly onRenderer?: (facts: RendererFacts) => void;
  readonly onHud: (hud: FlightHud) => void;
  /** The combat half of the HUD: enemy bearing/range and the outcome banner. */
  readonly onCombat: (combat: CombatHud) => void;
  readonly onMessages: (messages: readonly string[]) => void;
  /** Fired once the real `.WLD` region has loaded, for the HUD's region/start-aerodrome chip. */
  readonly onRegion: (summary: FlightRegionSummary) => void;
  /** Objectives, waypoint cue and outcome — only ever called when a mission is being flown. */
  readonly onMission?: (mission: MissionHud) => void;
  /** Fired once, on the tick a mission is decided, so the route can show the debrief. */
  readonly onMissionEnd?: (run: MissionRun) => void;
  /**
   * The instant dogfight's own version of `onMissionEnd` — fired once, on the
   * tick the bare encounter (no `MissionSpec`) is decided, so `/fly` on its
   * own can show a debrief too (`dogfightDebrief`, `docs/engineering/replay.md`
   * — this is what makes `SAVE FLIGHT` reachable there). Never fired for
   * `/duel` or `/vcr`: both pass their own `options.runner`, which is exactly
   * what `isDogfight` excludes.
   */
  readonly onDogfightEnd?: (encounter: EncounterState) => void;
  /** The cockpit panel's per-frame state; called only when it changes. */
  readonly onCockpitView?: (view: CockpitViewState) => void;
  /** Time compression's on/off edge, for the HUD chip (`time-compression.ts`) — the RB.EXE status string rides `onMessages` instead, same as every other in-flight callout. */
  readonly onTimeCompression?: (active: boolean) => void;
  /**
   * AD-015's kill camera opening and shutting, on the edges only. The picture
   * inside the corner window is a second render pass the scene owns
   * (`scene-killcam.ts`); this is what lets the DOM overlay draw the window's
   * frame around it and stand the controls hint down while it is up, since the
   * two want the same corner (`FlightSurface.tsx`).
   */
  readonly onKillCam?: (open: boolean) => void;
  /**
   * Every frame that produced whole sim ticks, with the stick they were all
   * flown with — the Mission Recorder's one hook into the flight loop
   * (`vcr/tape-store.ts`, `packages/engine/src/replay/`). `ticks` is never zero.
   *
   * Fired *after* the ticks have run, with the encounter as it stands, so a
   * recorder can take its checkpoint hash off the same state the frame ended
   * on. `inputs` is already quantised — it is exactly the stick the sim stepped
   * on, which is what makes the recording faithful rather than approximate.
   */
  readonly onFlown?: (inputs: FlightInputs, ticks: number, encounter: EncounterState) => void;
  /** The held keys for this frame, owned by the route (see `keyboard.ts`). */
  readonly pressedKeys: () => ReadonlySet<string>;
  /**
   * The mouse's contribution for this frame — held trigger and accumulated
   * wheel travel (see `pointer.ts`). Reading it consumes the wheel travel, so
   * the scene calls it exactly once per frame. The wheel is throttle in
   * cockpit/chase and fly-by camera zoom in `"flyBy"` (`scene-flyby-zoom.ts`
   * routes it). Optional: a caller with no pointer controls flies on the
   * keyboard alone.
   */
  readonly pointer?: () => PointerFrame;
  /**
   * Where a held right mouse button has the pilot looking, and whether he is
   * looking around at all (`free-look.ts`). Reading it advances the eased
   * return, so the scene calls it exactly once per frame, with that frame's
   * `dt`. Optional: a caller with no mouse flies on the six snap glances alone.
   *
   * **Camera only.** It is not folded into `pointer` precisely because a
   * `PointerFrame` goes on to `readFlightInputs` and this must not: nothing a
   * free look does may reach the sim, the Mission Recorder's tape or `/duel`'s
   * lockstep wire.
   */
  readonly freeLook?: (dtSeconds: number) => FreeLookFrame;
  /**
   * The joystick's contribution for this frame (see `gamepad.ts`). Reading it
   * polls the device and resolves the one-shot button edges, so — like
   * `pointer` — the scene calls it exactly once per frame, which is also the
   * only rate at which the browser refreshes a `Gamepad` snapshot. Optional: a
   * caller with no stick flies on the keyboard and mouse alone.
   */
  readonly gamepad?: () => PadFrame;
}

export interface FlightSceneOptions {
  readonly audio?: AudioSettings;
  /**
   * Which backend to build, decided already. Omitted — which is every route —
   * the scene asks `three/renderer-plan.ts` for itself; supplied, nothing reads
   * `location`, `navigator.gpu` or the settings store, which is what lets a test
   * drive each failure mode without stubbing three globals
   * (`renderer-fallback.test.ts`).
   */
  readonly rendererPlan?: RendererPlan;
  /** Port-extension visual effects (AD-012's propeller disc). Omitted, `DEFAULT_EFFECTS_SETTINGS` applies. */
  readonly effects?: EffectsSettings;
  /**
   * Where 3D COCKPIT's dashboard quad reads the composed cockpit frame from
   * (`scene-cockpit-3d.ts`). The route that mounts both the scene and the
   * `CockpitPanel` creates one and hands it to each; a scene with no panel over
   * it omits this and its quad stays hidden for want of a texture, which is the
   * right answer for a test and for anything headless.
   */
  readonly dashFeed?: DashCanvasFeed;
  /**
   * Pins the sky treatment, ignoring `effects.enhancedSky` — `/fly?sky=<name>`,
   * and the only way to reach the `measured` byte-comparison reference now that
   * it is off the settings screen (`effects-settings.ts`'s `SKY_TREATMENTS`).
   * A debug hatch: nothing links to it and nothing persists it.
   */
  readonly skyOverride?: SkyTreatment;
  /** AD-007n change 4: which keyboard-stick shaping to fly with. Defaults to the original's ramped axis. */
  readonly stickShaping?: StickShapingId;
  /** AD-021's BAIL OUT, as stored. Defaults on — see `flight/bail-settings.ts` on why. */
  readonly bailEnabled?: boolean;
  /**
   * I12's ENHANCED SIMULATION, as stored. **Defaults off**, which is the one
   * default on this interface that is off and the reason the engine is
   * bit-zero without it (`flight/sim-settings.ts`).
   *
   * Read once, when the runner is built, and never again: a sortie's ships are
   * deployed with its run. Flipping the setting reaches the *next* sortie.
   */
  readonly enhancedSimulation?: boolean;
  /**
   * A swap target for the region's own `GroundQuery`, shared with a runner the
   * caller built itself.
   *
   * `/fly` lets the scene own this ref, because the scene also builds the
   * runner. `/vcr` and `/duel` build their runner first and hand it in, so
   * without somewhere to share the ref their sim keeps flying over
   * `FLAT_GROUND` while the scene draws the region's relief — the replay would
   * then diverge from the tape it is replaying, over terrain it can see.
   *
   * The caller creates the ref, gives its runner a query that reads through it,
   * and passes it here; the scene fills it on region load, strictly before the
   * first tick, exactly as it does for its own.
   */
  readonly groundRef?: { current: GroundQuery };
  /** The scenery counterpart (AD-018), on the same terms — a caller that built its own runner shares its swap target. */
  readonly sceneryRef?: { current: SceneryQuery };
  /** The mission to fly. Omitted, the scene flies the instant 1v1 dogfight. */
  readonly mission?: MissionSpec;
  /**
   * A runner to fly instead of building one. `/duel` passes its lockstep
   * runner here (AD-008), which is the whole of what multiplayer costs the
   * renderer: same scene, same frame loop, same interpolation — a different
   * source of ticks.
   */
  readonly runner?: EncounterRunner;
  /**
   * Which `MissionRegion`'s scenery/ground to load (`fly-region.ts`). Defaults
   * to `mission?.region`, which is what `/fly` wants: the region rides the
   * `MissionSpec` and needs no separate wiring. `/vcr` passes this explicitly
   * instead — a replay supplies its own `runner` and never `mission`, so its
   * tape's briefed region (or `undefined`, for a bare-encounter tape) has
   * nowhere else to reach the scene from (`vcr-session.ts`'s `tapeRegion`).
   * `undefined` either way — the instant dogfight, and `/duel` — loads
   * `region-catalog.ts`'s `DEFAULT_FLY_REGION` (Verdun), matching every build
   * before per-mission regions existed.
   */
  readonly region?: MissionRegion;
  /**
   * What is under the aeroplanes. Flat datum by default, which is this port's
   * world today (`world.ts`'s one flat quad); the elevation lane implements the
   * engine's `GroundQuery` off `.GI` hill data and it is passed straight
   * through to the sim from here.
   */
  readonly ground?: GroundQuery;
  /**
   * Whether the player may engage time compression at all — `true` unless a
   * caller says otherwise. `/duel` passes `false`: its runner is `packages/net`'s
   * lockstep one, and running it ahead of real time would desync the two
   * peers exactly as surely as pausing one side would (`time-compression.ts`).
   */
  readonly timeCompressionAvailable?: boolean;
  /**
   * Whether AD-015's kill camera may open at all — `true` unless a caller says
   * otherwise. `/duel` passes `false`: a duel is the one competitive thing this
   * port has, and a window that opens itself over a quarter of the screen the
   * instant somebody is hit is a real advantage to whichever peer is not in it.
   * `/vcr` keeps it: a replay is exactly where watching a kill land is the point.
   */
  readonly killCamAvailable?: boolean;
  /**
   * Where the pilot last left AD-015's window (`killcam-layout.ts`), or
   * omitted/`null` for the default corner. Read once at scene creation; a live
   * drag comes through `setKillCamPlacement` instead.
   */
  readonly killCamPlacement?: KillCamPlacement | null;
  /**
   * The career pilot's paint-shop choices for his personal aircraft, as
   * `BARON.PAL` indices. Omitted, the factory scheme the model was baked in is
   * drawn — what a single mission, a duel and an unpainted career all want.
   */
  readonly playerPaint?: PersonalPaint;
  /**
   * The career pilot's own calendar date (`campaign/calendar.ts`), when a
   * career sortie is being flown.
   *
   * Only the ENHANCED night sky reads it, and it reads it for one thing: the
   * ephemeris `night-sky-look.ts` places the stars and the moon from. A single
   * mission has no date, only a `MissionSpec.era`, and derives one from the era
   * and its seed; a duel and the instant dogfight have neither and get one
   * fixed December 1915 night. Nothing about the flight itself changes with it.
   */
  readonly careerDate?: CareerDate;
  /**
   * The Realism Panel's SUN BLIND SPOT toggle (`mission/realism-settings.ts`'s
   * `PresentationRealism`, not the engine's `RealismSettings` — this is
   * view-layer, sourced separately from `mission.realism`). `false`/omitted
   * changes no flight, matching every other realism toggle's default. Needs
   * `mission` to do anything: an instant dogfight has no briefed time of day,
   * so there is no sun to fly into (`sun-blind-spot.ts`).
   */
  readonly sunBlindSpot?: boolean;
  /**
   * The Realism Panel's REAL NAVIGATION toggle. `false`/omitted changes no
   * flight. See `mission-hud.ts`'s `MissionHudOptions.hideWaypoint`.
   */
  readonly realNavigation?: boolean;
}

/** The handle `createFlightScene` returns. Nothing it owns outlives `dispose()`. */
export interface FlightScene {
  setCameraMode(mode: CameraMode): void;
  /**
   * Whose aeroplane the external cameras follow — `null` for the player's own,
   * the original's `OTHERS`/`SWITCH` for anyone else's (`camera-target.ts`).
   * Only the external modes honour it: there is no sitting in another pilot's
   * cockpit, and asking for one leaves the camera where it was.
   */
  setCameraTarget(target: CameraTarget): void;
  /** Stops the sim *and* the mix — a paused flight is silent (`audio/pause-gate.ts`). */
  setPaused(paused: boolean): void;
  setAudioSettings(settings: AudioSettings): void;
  /** Turns the port-extension effects on or off live — purely cosmetic, nothing the sim can see. */
  setEffectsSettings(settings: EffectsSettings): void;
  /** AD-007n change 4: swap the keyboard-stick shaping without disturbing where the axes currently sit. */
  setStickShaping(shaping: StickShapingId): void;
  /**
   * AD-021: whether `H` can put the pilot over the side. Off means the key is
   * inert, and any part-completed hold is discarded — a player who switches the
   * feature off mid-sortie cannot have half-armed it already.
   */
  setBailEnabled(enabled: boolean): void;
  /**
   * AD-021's jump, fired from somewhere other than the held `H` — G38 put a BAIL
   * OUT row on the in-flight `ESC` board, and this is how that row reaches the
   * sim.
   *
   * **The same command, not a second one.** It calls `EncounterRunner.bailOut`,
   * exactly as `scene-loop.ts`'s hold does, so the engine's own refusals are the
   * only rules in play and there is nowhere for two entry points to disagree.
   * Ignores `setBailEnabled`: the preference gates the *affordance*, and a row
   * that is drawn has already been gated by the thing that drew it.
   */
  bailOut(): void;
  /**
   * End the sortie now and go to the debriefing — G38's RETURN TO BASE.
   *
   * The one imperative way out of a flight, and it exists because there was
   * none: `mission-report.ts` fires `onMissionEnd` only when the *run* decides
   * itself, so a player who simply wanted to go home had no exit but the route
   * link, which leaves without a debriefing at all.
   *
   * Goes through the same latches the natural ending does (`missionEnded` /
   * `dogfightEnded`), because those latches are what `silentFlight` reads — a
   * debriefing raised any other way would have the engine still droning behind
   * it, which is the exact defect `scene-audio.ts` documents.
   *
   * A no-op once the sortie has ended by itself: the outcome is already recorded
   * and re-reporting it would credit the career twice.
   */
  endSortie(): void;
  /** Requests compression on/off; a no-op if `timeCompressionAvailable` was `false`, and refused (with the "NOT AVAILABLE NOW" message) while an enemy is close — see `time-compression.ts`. */
  setTimeCompression(active: boolean): void;
  /**
   * Moves or resizes AD-015's corner window, live. Called on every pointer move
   * of a title-bar drag, so the picture travels with the frame the DOM is
   * drawing around it; `null` restores the default corner.
   */
  setKillCamPlacement(placement: KillCamPlacement | null): void;
  /**
   * The × on that window's title bar: no window opens again for the rest of
   * this sortie. Turning KILL CAMERA on in PREFERENCES clears it — closing is a
   * dismissal, the setting is the switch (`docs/engineering/killcam-design.md`).
   */
  dismissKillCam(): void;
  dispose(): void;
}

// Re-exported so a route can take the scene and its view table from one import.
export { cameraModeForCommand, DEFAULT_CAMERA_MODE, nextCameraMode, type CameraMode } from "./camera-modes.ts";
